// AppShell breadcrumb row (Spec §6.1) — "Workspace › Section › Screen".
// Wayfinding was entirely absent before the redesign; this is the single
// biggest orientation win. Derived from the active workspace nav + pathname.

import { Link, useLocation } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import { useSpine } from '../spine'
import { WORKSPACE_NAV, navLinkForPath } from '../../config/navigation'

export interface Crumb {
  label: string
  to?: string
}

/** Compute the breadcrumb trail for a pathname within the active workspace. */
export function useBreadcrumbs(): Crumb[] {
  const { active } = useWorkspace()
  const { pathname, search } = useLocation()
  const nav = WORKSPACE_NAV[active.kind]
  // Bug #5837: the atlas read "Personal > Innovations > Projects" because the
  // query string was dropped here too. One resolver, one fix, both surfaces.
  const link = navLinkForPath(nav, pathname, search)

  const crumbs: Crumb[] = [{ label: active.name, to: '/' }]
  if (link?.section && link.section !== link.label) {
    crumbs.push({ label: link.section })
  }
  if (link) {
    crumbs.push({ label: link.label, to: link.to })
  }
  // Deep sub-routes (e.g. /project/:id, /document/:id) get a trailing crumb so
  // the user sees they have drilled in, even before that screen sets its own.
  if (link && pathname !== link.to && !link.to.endsWith(pathname)) {
    const tail = pathname.split('/').filter(Boolean).slice(-1)[0]
    if (tail && !link.to.includes(tail)) {
      crumbs.push({ label: 'Detail' })
    }
  }
  return crumbs
}

export default function Breadcrumbs({ trail }: { trail?: Crumb[] }) {
  const auto = useBreadcrumbs()
  // Story #5648: a graduated screen states its own place on the spine and the
  // shell draws ONE crumb line for it (PageCrumb). This derived trail would be
  // a second, competing line — the "Personal › Innovations › Projects › Detail"
  // row stacked above the page's own — so it stands down entirely.
  const spine = useSpine()
  const crumbs = trail ?? auto
  if (spine) return null
  if (crumbs.length <= 1) return null
  return (
    <nav aria-label="Breadcrumb" className="mb-3">
      <ol className="flex flex-wrap items-center gap-1 text-sm text-slate-500">
        {crumbs.map((c, i) => {
          const last = i === crumbs.length - 1
          return (
            <li key={`${c.label}-${i}`} className="flex items-center gap-1">
              {i > 0 && <ChevronRight size={14} className="text-slate-400" aria-hidden />}
              {c.to && !last ? (
                <Link to={c.to} className="hover:text-iflow-700 hover:underline">
                  {c.label}
                </Link>
              ) : (
                <span className={last ? 'font-medium text-iflow-900' : undefined} aria-current={last ? 'page' : undefined}>
                  {c.label}
                </span>
              )}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
